import type { StorageAdapter } from "@hops/adobe-panel-core";
import {
  configureClient,
  createWebviewEndpoint,
  getPersistedServerUrl,
  initAuthStorage,
  initServerUrlStorage,
  initTimerStorage,
  MockHost,
  PostMessageHost,
  restoreAuth,
  restoreTimer,
} from "@hops/adobe-panel-core";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React from "react";
import ReactDOM from "react-dom/client";
import "@hops/adobe-panel-core/src/ui/styles.css";
import { App } from "./App";
import "./app.css";

const API_BASE_URL = import.meta.env.PROD
  ? "https://hops.next.hodgesbadge.com"
  : "http://localhost:5050";

// Detect environment: UXP webview vs standalone browser
const isWebview = typeof window.uxpHost !== "undefined";

const host = isWebview
  ? new PostMessageHost(createWebviewEndpoint())
  : new MockHost();

// Webview uses localStorage; standalone uses in-memory
class WebviewStorageAdapter implements StorageAdapter {
  private prefix = "hops-panel-";

  async getItem(key: string): Promise<string | null> {
    return localStorage.getItem(this.prefix + key);
  }

  async setItem(key: string, value: string): Promise<void> {
    localStorage.setItem(this.prefix + key, value);
  }

  async removeItem(key: string): Promise<void> {
    localStorage.removeItem(this.prefix + key);
  }
}

const storage = isWebview
  ? new WebviewStorageAdapter()
  : new WebviewStorageAdapter(); // localStorage works in browser too

initAuthStorage(storage);
initTimerStorage(storage);
initServerUrlStorage(storage);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

async function init() {
  const persistedUrl = await getPersistedServerUrl();
  const serverUrl = persistedUrl || API_BASE_URL;
  configureClient(serverUrl);

  await restoreAuth();
  await restoreTimer();

  ReactDOM.createRoot(document.getElementById("app")!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <App host={host} defaultServerUrl={serverUrl} />
      </QueryClientProvider>
    </React.StrictMode>,
  );
}

init();
